const escapeHtml = (value) => String(value ?? '').replace(/[&<>"']/g, char => ({
  '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
}[char]));

const compare = (a, b) => String(a).localeCompare(String(b), 'fr', { numeric: true, sensitivity: 'base' });
const stockOf = item => item.article?.stocks?.[0];
const STANDARD_FLOORS = ['-1', '0', '1', '2', '3', '4'];
const ROUTE_FLOORS = [...STANDARD_FLOORS, ''];
const floorOf = item => {
  const floor = String(stockOf(item)?.Etage ?? '').trim();
  return STANDARD_FLOORS.includes(floor) ? floor : '';
};
const compareFloors = (a, b) => ROUTE_FLOORS.indexOf(a) - ROUTE_FLOORS.indexOf(b);

// Keep each devis separate, but order the entire job by floor first.
export const buildVentileGroups = (documents, deliveryModes = []) => {
  const result = [];
  // The last, unnamed slot collects all items outside the six named floors.
  documents.forEach(({ devis, items }, documentIndex) => {
    if (!devis || !Array.isArray(items) || items.length === 0) {
      throw new Error(`Aucun article à imprimer pour le devis ${devis?.REF_DEV || ''}.`);
    }
    const floors = new Map();
    items.forEach(item => {
      const floor = floorOf(item);
      if (!floors.has(floor)) floors.set(floor, []);
      floors.get(floor).push(item);
    });
    const visitedFloors = ROUTE_FLOORS.map(etage => ({ etage, count: floors.get(etage)?.length || 0 }));
    const modeName = String(devis.MODELIV || '').trim().toLocaleUpperCase('fr');
    const deliveryRemark = deliveryModes.find(mode => String(mode.name).trim().toLocaleUpperCase('fr') === modeName)?.desc || '';
    floors.forEach((floorItems, etage) => {
      result.push({
        devis, documentIndex, etage, visitedFloors, deliveryRemark, totalItems: items.length,
        items: [...floorItems].sort((a, b) => {
          const zoneA = String(stockOf(a)?.Zone || '').trim();
          const zoneB = String(stockOf(b)?.Zone || '').trim();
          return (!zoneA && zoneB ? 1 : zoneA && !zoneB ? -1 : compare(zoneA, zoneB)) || compare(a.CODE_ART || '', b.CODE_ART || '');
        }),
      });
    });
  });
  return result.sort((a, b) => compareFloors(a.etage, b.etage) || a.documentIndex - b.documentIndex);
};

const formatDate = value => {
  if (!value) return '';
  // These are document calendar dates; UTC midnight must not become the previous day.
  const calendarDate = String(value).match(/^(\d{4})-(\d{2})-(\d{2})(?:T|$)/);
  if (calendarDate) return `${calendarDate[3]}/${calendarDate[2]}/${calendarDate[1]}`;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString('fr-FR');
};

const renderGroup = group => {
  const { devis, etage, items, visitedFloors, totalItems, deliveryRemark, documentIndex } = group;
  const remarks = [devis.REMARQUE, deliveryRemark]
    .filter(value => String(value ?? '').trim())
    .map(escapeHtml)
    .join(' — ');
  return `<section class="ventile-page" data-document="${documentIndex}" data-etage="${escapeHtml(etage)}">
    <header>
      <div class="route-header">
        <div class="total-items">0</div>
        <div class="route-label"><i>Les étages à visiter :</i><br><i>Range</i> <b title="Total des articles du devis">(${totalItems})</b></div>
        <div class="floors" style="grid-template-columns: repeat(${visitedFloors.length}, minmax(0, 1fr))">${visitedFloors.map(floor => `<div class="floor" data-floor="${escapeHtml(floor.etage)}"><div class="floor-box${floor.etage === etage ? ' current' : ''}">${floor.count ? escapeHtml(floor.etage) : '-'}</div><b>${floor.count || '-'}</b></div>`).join('')}</div>
        <div class="floor-title"><i>Étage :</i> <strong>${escapeHtml(etage)}</strong><div class="page-number" data-page-number></div></div>
      </div>
      <div class="reference-row"><strong>${escapeHtml(devis.REF_DEV)}</strong><span class="delivery"><b>${escapeHtml(devis.MODELIV)}</b> <small>${escapeHtml(devis.MODE_PAIE)}</small></span><span class="date">${formatDate(devis.DATEVALID || devis.DATE_DEV)}${devis.HEUREVALID ? ` - ${escapeHtml(devis.HEUREVALID)}` : ''}</span></div>
      <div class="client-row"><span>${escapeHtml(devis.COMPTE)} &nbsp; <b>${escapeHtml(devis.CLIENT)}</b></span><i>Prép. par : ......................................</i></div>
      <div class="remark-row"><div><i>Remarq :</i> <span class="remark">${remarks}</span></div><i>Ctrl. par : ........................ à ................</i></div>
    </header>
    <table class="ventile-items">
      <colgroup><col style="width:15%"><col style="width:67%"><col style="width:6%"><col style="width:6%"><col style="width:6%"></colgroup>
      <thead><tr><th>CODE ART</th><th><span class="article-heading">ARTICLE / Ref_Oem</span><span class="stock-heading">Stk - Cart - Paquet</span></th><th>QTE</th><th>Prép. X</th><th>Ctrl H</th></tr></thead>
      <tbody>${items.map(item => `<tr>
        <td>${escapeHtml(item.CODE_ART)}<div class="secondary">${escapeHtml(stockOf(item)?.Zone)}</div></td>
        <td><div class="designation">${escapeHtml(item.ARTICLE)}</div><div class="article-details"><span>${escapeHtml(item.article?.REF_OEM)}</span><b>${escapeHtml(stockOf(item)?.qte_stk ?? 0)} - ${escapeHtml(item.article?.qt_cart ?? 0)} - ${escapeHtml(item.article?.q_paquet ?? 0)}</b></div></td>
        <td class="quantity">${escapeHtml(item.QTE)}</td><td></td><td></td>
      </tr>`).join('')}</tbody>
    </table>
    <footer>${escapeHtml(devis.REF_DEV)} / ${escapeHtml(etage)} <span data-footer-page></span></footer>
  </section>`;
};

export const buildVentileHtml = (documents, deliveryModes = []) => `<!DOCTYPE html>
<html lang="fr"><head><meta charset="UTF-8"><title>Devis ventilés — A5</title><style>
  @page { size: A5 landscape; margin: 5mm; }
  * { box-sizing: border-box; }
  body { margin: 0; color: #000; background: #fff; font: 10pt Arial, sans-serif; }
  .ventile-page { width: 200mm; height: 138mm; padding: 0; margin: 0 auto; break-after: page; page-break-after: always; }
  .ventile-page:last-child { break-after: auto; page-break-after: auto; }
  i, th { font-family: 'Times New Roman', serif; font-weight: bold; }
  .route-header { display: flex; align-items: center; gap: 3mm; min-height: 14mm; }
  .total-items { font-size: 34pt; font-weight: bold; flex: 0 0 18mm; text-align: center; }
  .route-label { line-height: 1.6; white-space: nowrap; flex: 0 0 37mm; }
  .route-label b { margin-left: 5mm; }
  .floors { display: grid; flex: 1; min-width: 0; gap: 0; }
  .floor { min-width: 0; text-align: center; font-size: 10pt; overflow-wrap: anywhere; }
  .floor-box { border: .2mm solid #777; padding: 1mm; height: 7mm; display: flex; align-items: center; justify-content: center; }
  .floor-box.current { border-color: #000; font-weight: bold; }
  .floor > b { display: block; text-decoration: underline; padding-top: .5mm; }
  .floor-title { flex: 0 0 30mm; text-align: center; font-size: 14pt; }
  .floor-title strong { margin-left: 3mm; }
  .page-number { font: italic bold 10pt/4mm 'Times New Roman', serif; min-height: 4mm; margin-top: 1mm; }
  .reference-row { display: flex; align-items: baseline; justify-content: space-between; gap: 3mm; border-top: .2mm solid #999; border-bottom: .2mm solid #999; padding: 1mm 0; }
  .reference-row > strong, .delivery > b { font-size: 19pt; }
  .delivery { overflow-wrap: anywhere; }
  .delivery small { font-size: 10pt; }
  .date { white-space: nowrap; font-size: 9pt; }
  .client-row, .remark-row { display: flex; justify-content: space-between; gap: 3mm; padding: 1.2mm 0; align-items: baseline; }
  .client-row > span, .remark-row > div { flex: 1; overflow-wrap: anywhere; min-width: 0; }
  .client-row > i, .remark-row > i { flex: 0 0 70mm; font-size: 10pt; }
  .remark { margin-left: 3mm; white-space: pre-wrap; }
  .ventile-items { width: 100%; border-collapse: collapse; table-layout: fixed; border-top: .6mm solid #000; }
  th { font-size: 8pt; font-style: italic; text-align: left; padding: .4mm .7mm; border-bottom: .5mm solid #000; }
  th:nth-child(n+3) { text-align: center; }
  .stock-heading { float: right; }
  td { border: .4mm solid #000; padding: .45mm .7mm; font-size: 8pt; line-height: 1.1; vertical-align: top; overflow-wrap: anywhere; }
  td:first-child { border-left: 0; }
  td:last-child { border-right: 0; }
  .designation { white-space: nowrap; font-size: 8pt; }
  .secondary, .article-details { margin-top: .25mm; font-size: 7pt; }
  .article-details { display: flex; justify-content: space-between; gap: 1mm; }
  .article-details b { white-space: nowrap; padding-right: 1mm; }
  .quantity { font-weight: bold; text-align: right; }
  tr, header { break-inside: avoid; page-break-inside: avoid; }
  footer { text-align: right; font-size: 8pt; border-bottom: .4mm dotted #000; padding: 1mm 0; }
  @media screen { body { background: #ddd; padding: 5mm; } .ventile-page { background: white; margin-bottom: 5mm; box-shadow: 0 0 0 5mm white; } }
</style></head><body>${buildVentileGroups(documents, deliveryModes).map(renderGroup).join('')}</body></html>`;

// Measure wrapped rows at the real A5 width; repeat the full header on every page.
export const paginateVentileDocument = async doc => {
  await doc.fonts?.ready;
  const sources = [...doc.querySelectorAll('.ventile-page')];
  const allPages = [];
  for (const source of sources) {
    // Fit the complete designation on one line before measuring row heights.
    source.querySelectorAll('.designation').forEach(designation => {
      const availableWidth = designation.clientWidth;
      if (availableWidth > 0 && designation.scrollWidth > availableWidth) {
        const fontSize = parseFloat(doc.defaultView.getComputedStyle(designation).fontSize);
        designation.style.fontSize = `${fontSize * availableWidth / designation.scrollWidth * 0.98}px`;
      }
    });
    const groupPages = [];
    const rows = [...source.querySelector('tbody').rows];
    const template = source.cloneNode(true);
    template.querySelector('tbody').replaceChildren();
    let page;
    let body;
    const newPage = () => {
      page = template.cloneNode(true);
      source.before(page);
      body = page.querySelector('tbody');
      groupPages.push(page);
      allPages.push(page);
    };
    const fits = () => page.querySelector('footer').getBoundingClientRect().bottom <= page.getBoundingClientRect().bottom - 2;
    newPage();
    for (const row of rows) {
      body.append(row);
      if (!fits()) {
        row.remove();
        if (body.rows.length === 0) throw new Error('Une ligne ou un en-tête dépasse la hauteur A5 imprimable.');
        newPage();
        body.append(row);
        if (!fits()) throw new Error('Une ligne ou un en-tête dépasse la hauteur A5 imprimable.');
      }
    }
    source.remove();
    // Each source contains one devis on one floor.
    groupPages.forEach((page, index) => {
      page.querySelector('[data-footer-page]').textContent = ` — ${index + 1}/${groupPages.length}`;
    });
  }
  // Header numbering follows the full print run in physical page order.
  allPages.forEach((page, index) => {
    page.querySelector('[data-page-number]').textContent = `${index + 1}/${allPages.length}`;
  });
};
